// packages/api/src/requestTimeout.ts
//
// ROADMAP #122 — A REQUEST THAT NEVER ANSWERS MUST STILL END.
//
// ⚠⚠ WHY. The app's GraphQL requests had no time limit. Since #121 a drawer refuses every way out while it saves —
// Cancel, the X, the drag handle, Esc, a click outside — so a mutation that never answered (a phone losing signal
// mid-save, a server that hung) left the drawer spinning until a reload, with the user's work trapped inside it.
// A timeout here ENDS the request as an ordinary refusal: the caller's `finally` runs, the spinner clears, the drawer
// lets go, and the work is still in the form.
//
// ⚠ READS ARE TIMED TOO, on a longer limit. A read can trap people as well: some saves await a read inside their locked
// window (a retry re-reading what the first try left). A read is safe to repeat, so its message just says try again; a
// slow whole-set read pages, so each request is small.
//
// ⚠ A WRITE that timed out is announced (`writeTimeoutSignal.ts`, from ApiService) so every list on screen re-reads and
// the host can see whether it landed (ROADMAP #131).
//
// ⚠ The limit covers the BODY too, not just the headers. `fetch` resolves when the headers arrive; a server that sends
// them and then stalls would still hang the request. So a timed request reads its body inside the limit and hands back
// a buffered copy (a GraphQL answer is one JSON document — nothing here streams).
//
// ⚠ An abandoned MUTATION may still have been applied: the server can finish it after we stop listening. So its message
// says so, and never claims it failed. A mutation whose work grows with what the user pasted (a guest import) gets its
// own, longer limit by operation name.
//
// Reusable by any client that posts GraphQL with `fetch` — the app's Apollo HttpLink, and events-web's browser clients
// (`lib/requestLimits.ts`, ROADMAP #131); `fetchWithLimit` is the same limit for one plain REST call.

/** What the user reads when a write gets no answer. Honest that it may have landed anyway. */
export const REQUEST_TIMEOUT_MESSAGE = "No answer from the server. It may still have gone through — check before trying again.";

/** What the user reads when a read gets no answer. A read changes nothing, so trying again is safe. */
export const READ_TIMEOUT_MESSAGE = "No answer from the server. Check your connection and try again.";

/** The error a timed-out request fails with — its own name, so nothing mistakes it for a user's cancel (AbortError). */
export class RequestTimeoutError extends Error {
  readonly timeoutMs: number;
  /** A write (or anything not known to be a read): the server may have applied it. */
  readonly isWrite: boolean;

  constructor(timeoutMs: number, isWrite: boolean) {
    super(isWrite ? REQUEST_TIMEOUT_MESSAGE : READ_TIMEOUT_MESSAGE);
    this.name = "RequestTimeoutError";
    this.timeoutMs = timeoutMs;
    this.isWrite = isWrite;
  }
}

type TimeoutLike = { name?: unknown; isWrite?: unknown; networkError?: unknown; cause?: unknown; error?: unknown };

/**
 * The request timeout this error is, or wraps — or null. Apollo may hand the error back as-is, as `networkError`, as
 * `cause`, or inside a resolved result's `error`, so look a few levels down.
 */
export const findRequestTimeout = (error: unknown, depth = 0): TimeoutLike | null => {
  if (!error || typeof error !== "object" || depth > 4) {
    return null;
  }

  const e = error as TimeoutLike;

  if (e.name === "RequestTimeoutError") {
    return e;
  }

  for (const inner of [e.networkError, e.cause, e.error]) {
    const found = findRequestTimeout(inner, depth + 1);
    if (found) {
      return found;
    }
  }

  return null;
};

/** Is this (or something it wraps) a request timeout? */
export const isRequestTimeout = (error: unknown): boolean => findRequestTimeout(error) !== null;

/** Is this OpResult a request that ran out of time (ApiService's refusal, or a stalled upload's)? */
export const isTimeoutResult = (result: { errors?: Array<{ errors?: unknown[] }> } | null | undefined): boolean =>
  (result?.errors ?? []).some((item) => (item?.errors ?? []).some((m) => m === REQUEST_TIMEOUT_MESSAGE || m === READ_TIMEOUT_MESSAGE));

/** The message for a timeout: the read one only when it is KNOWN to be a read — otherwise the cautious write one. */
export const requestTimeoutMessage = (error: unknown): string => {
  return findRequestTimeout(error)?.isWrite === false ? READ_TIMEOUT_MESSAGE : REQUEST_TIMEOUT_MESSAGE;
};

const NAME_START = /[A-Za-z_]/;
const NAME_PART = /[A-Za-z0-9_]/;

const OPERATION_KEYWORDS = new Set(["query", "mutation", "subscription"]);

/**
 * What a GraphQL document's top-level definitions say: whether any is a MUTATION, and the name of the first operation
 * (the one a client sends when it does not name one). Skips comments, strings and selection sets — so a document that
 * opens with a fragment (`fragment F on T {…} mutation M {…}`) is still a mutation, and a field, variable or string that
 * merely says "mutation" is not.
 */
export const readOperation = (query: string): { isMutation: boolean; name: string | null } => {
  const n = query.length;
  let depth = 0;
  let i = 0;
  let isMutation = false;
  let name: string | null = null;
  // The keyword of an operation whose name may come next (and only next).
  let awaitingName = false;
  // Inside a fragment's header (`fragment query on T`) or a variable list (`($s: Scope = mutation)`), a word is a name or
  // a value, never an operation keyword.
  let inFragmentHeader = false;
  let parens = 0;

  while (i < n) {
    const c = query[i];

    if (c === "#") {
      while (i < n && query[i] !== "\n") {
        i += 1;
      }
      continue;
    }

    if (c === '"') {
      awaitingName = false;
      if (query.startsWith('"""', i)) {
        const end = query.indexOf('"""', i + 3);
        i = end < 0 ? n : end + 3;
        continue;
      }

      i += 1;
      while (i < n && query[i] !== '"') {
        i += query[i] === "\\" ? 2 : 1;
      }
      i += 1;
      continue;
    }

    if (c === "{") {
      awaitingName = false;
      inFragmentHeader = false;
      depth += 1;
      i += 1;
      continue;
    }

    if (c === "}") {
      depth = Math.max(0, depth - 1);
      i += 1;
      continue;
    }

    if (NAME_START.test(c)) {
      let j = i + 1;
      while (j < n && NAME_PART.test(query[j])) {
        j += 1;
      }

      const word = query.slice(i, j);

      if (depth === 0 && parens === 0 && !inFragmentHeader && query[i - 1] !== "$") {
        if (word === "fragment") {
          inFragmentHeader = true;
          awaitingName = false;
        } else if (awaitingName) {
          name = name ?? word;
          awaitingName = false;
        } else if (OPERATION_KEYWORDS.has(word)) {
          isMutation = isMutation || word === "mutation";
          awaitingName = name === null;
        }
      }

      i = j;
      continue;
    }

    if (c === "(") {
      awaitingName = false;
      parens += 1;
    }

    if (c === ")") {
      parens = Math.max(0, parens - 1);
    }

    // A directive (`mutation @client { … }`) is not the operation's name.
    if (c === "@") {
      awaitingName = false;
    }

    i += 1;
  }

  return { isMutation, name };
};

/** Does this GraphQL document define a MUTATION? (See `readOperation`.) */
export const isMutationDocument = (query: string): boolean => readOperation(query).isMutation;

/** What a request body says it is: its document text and operation name, when it is a readable GraphQL body. */
const readGraphqlBody = (body: unknown): { query: string | null; operationName: string | null } | null => {
  if (typeof body !== "string") {
    return null;
  }

  try {
    const parsed = JSON.parse(body) as { query?: unknown; operationName?: unknown };

    return {
      query: typeof parsed?.query === "string" ? parsed.query : null,
      operationName: typeof parsed?.operationName === "string" ? parsed.operationName : null,
    };
  } catch {
    return null;
  }
};

/** Is this request body a GraphQL MUTATION? (`{"query":"…mutation…"}` — anything unreadable is not.) */
export const isMutationBody = (body: unknown): boolean => {
  const parsed = readGraphqlBody(body);

  return !!parsed?.query && isMutationDocument(parsed.query);
};

/** Statuses that may not carry a body — a buffered copy of one must be built without it. */
const NULL_BODY_STATUSES = [204, 205, 304];

export interface TimeoutFetchOptions {
  /** How long a mutation may take, headers AND body, before it is abandoned. */
  mutationTimeoutMs: number;
  /** How long anything else (a read) may take — `null` leaves reads untimed. Required: say which you mean. */
  readTimeoutMs: number | null;
  /** A longer limit for named mutations whose work grows with the input (an import of pasted rows). */
  slowMutationsMs?: Readonly<Record<string, number>>;
  /** The fetch to wrap (defaults to the global one at call time). */
  fetchImpl?: typeof fetch;
}

export interface FetchLimit {
  /** How long the request may take, headers AND body, before it is abandoned. */
  limitMs: number;
  /** A write (it may still have been applied — the honest message) or a read (safe to try again). */
  isWrite: boolean;
  /** The fetch to use (defaults to the global one at call time). */
  fetchImpl?: typeof fetch;
}

/**
 * ONE request with a limit: aborted, and rejected with a `RequestTimeoutError`, when it has not answered — body included
 * — within `limitMs`. A caller's own abort still reaches it. For a plain REST call (a game action, a lookup); a GraphQL
 * client takes `createTimeoutFetch`, which picks the limit from the operation.
 */
export const fetchWithLimit = (input: RequestInfo | URL, init: RequestInit = {}, { limitMs, isWrite, fetchImpl }: FetchLimit): Promise<Response> => {
  const doFetch = fetchImpl ?? fetch;
  const controller = new AbortController();
  const upstream = init.signal ?? null;
  const onUpstreamAbort = () => controller.abort(upstream?.reason);

  if (upstream) {
    if (upstream.aborted) {
      controller.abort(upstream.reason);
    } else {
      upstream.addEventListener("abort", onUpstreamAbort, { once: true });
    }
  }

  return new Promise<Response>((resolve, reject) => {
    let settled = false;
    const settle = (fn: () => void) => {
      if (settled) {
        return;
      }

      settled = true;
      clearTimeout(timer);
      upstream?.removeEventListener("abort", onUpstreamAbort);
      fn();
    };

    // Only a request still waiting is abandoned: one that answered is never aborted afterwards.
    const timer = setTimeout(() => {
      const error = new RequestTimeoutError(limitMs, isWrite);
      settle(() => {
        reject(error);
        controller.abort(error);
      });
    }, limitMs);

    // `then` first, so a fetch that throws synchronously still ends through `settle` (and clears the timer).
    Promise.resolve()
      .then(() => doFetch(input, { ...init, signal: controller.signal }))
      .then(async (response) => {
        // A status a Response cannot be rebuilt with (0 for an opaque answer) goes back as it came.
        if (response.status < 200 || response.status > 599) {
          settle(() => resolve(response));

          return;
        }

        // Read the body INSIDE the limit (see the header), then hand back a buffered copy.
        const text = NULL_BODY_STATUSES.includes(response.status) ? null : await response.text();
        const copy = new Response(text, { status: response.status, statusText: response.statusText, headers: response.headers });
        settle(() => resolve(copy));
      })
      .catch((error) => {
        settle(() => reject(error));
      });
  });
};

/**
 * The limit a request body gets under these options: a mutation the general write limit, or its own when it is named in
 * `slowMutationsMs`; anything else the read limit (`null`: untimed). ⚠ The name is the body's `operationName` — or, when
 * the client sends none (events-web's browser clients post only `{ query, variables }`), the document's own: reading
 * only the field left the payment steps on the general limit (#131 review).
 */
export const requestLimitFor = (
  body: unknown,
  { mutationTimeoutMs, readTimeoutMs, slowMutationsMs }: Omit<TimeoutFetchOptions, "fetchImpl">
): { limitMs: number | null; isWrite: boolean } => {
  const parsed = readGraphqlBody(body);
  const operation = parsed?.query ? readOperation(parsed.query) : { isMutation: false, name: null };

  if (!operation.isMutation) {
    return { limitMs: readTimeoutMs, isWrite: false };
  }

  const name = parsed?.operationName ?? operation.name;

  return { limitMs: (name && slowMutationsMs?.[name]) || mutationTimeoutMs, isWrite: true };
};

/**
 * A `fetch` that abandons a GraphQL request after its limit: it aborts the request and rejects with a
 * `RequestTimeoutError`. A caller's own abort (Apollo unsubscribing) still reaches the request.
 */
export const createTimeoutFetch = ({ mutationTimeoutMs, readTimeoutMs, slowMutationsMs, fetchImpl }: TimeoutFetchOptions): typeof fetch => {
  return (input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> => {
    const doFetch = fetchImpl ?? fetch;
    const { limitMs: limit, isWrite } = requestLimitFor(init.body, { mutationTimeoutMs, readTimeoutMs, slowMutationsMs });

    if (limit == null) {
      return doFetch(input, init);
    }

    return fetchWithLimit(input, init, { limitMs: limit, isWrite, fetchImpl: doFetch });
  };
};
